import { useToast } from 'primevue/usetoast'

export default function useHelpers() {
  const { t } = useI18n()
  const toast = useToast()
  const config = useRuntimeConfig()
  const { accessToken, clearUser } = useUserStore()
  const router = useRouter()
  const { undoDelete, undoSave } = storeToRefs(useUserStore())

  interface FetchOptions {
    baseUrl?: string
    fetchOptions?: any
  }

  const fetchBase = async <T>(
    url: string,
    options: FetchOptions = {
      baseUrl: config.public.baseURL ?? '',
      fetchOptions: {}
    }
  ) => {
    let data, error
    try {
      data = await $fetch(url, {
        baseURL: options?.baseUrl ?? config.public.baseURL,
        initialCache: false,
        ...options?.fetchOptions,
        headers: {
          'Access-Control-Allow-Origin': '*',
          ...options?.fetchOptions.headers
        }
      })
    } catch (e) {
      error = e

      if (error.statusCode === 401) {
        // no longer authenticated
        clearUser()
        await router.push('/login')
      }
    }

    return { data: data as T, error: error }
  }

  const fetchAuth = <T>(
    url: string,
    options: FetchOptions = {
      baseUrl: config.public.baseURL ?? '',
      fetchOptions: {}
    }
  ) => {
    return fetchBase<T>(url, {
      baseUrl: options?.baseUrl ?? config.public.baseURL,
      fetchOptions: {
        ...options?.fetchOptions,
        headers: {
          Authorization: accessToken,
          ...options?.fetchOptions.headers
        }
      }
    })
  }

  const addErrorToast = (message?: string | null) => {
    toast.add({
      severity: 'error',
      summary: message ?? t('c_Toast.Something went wrong, try again'),
      life: 3000,
      closable: false
    })
  }

  const addSuccessToast = (message: string) => {
    toast.add({
      severity: 'success',
      summary: message,
      life: 3000,
      closable: false
    })
  }

  function addUndoDeleteToast(message: string, uuid: string) {
    undoDelete.value = true
    toast.add({
      severity: 'success',
      summary: message,
      detail: uuid,
      life: 10000,
      closable: false
    })
  }

  function addUndoSaveToast(message: string, type: string) {
    undoSave.value = true
    toast.add({
      severity: 'success',
      summary: message,
      detail: type,
      life: 10000,
      closable: false
    })
  }

  return {
    fetchBase,
    fetchAuth,
    addErrorToast,
    addSuccessToast,
    addUndoDeleteToast,
    addUndoSaveToast
  }
}
